import React, { useState, useRef, useEffect } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { sidebarMenu } from "../config/sidebarConfig";
import { images } from "../assets/images";
import { FaAngleRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { clearCookies } from "../config/varConfig";
import { logout } from "../redux/features/authSlice";
// import "./styles/Sidebar.css";

// Helper function to safely clone the menu structure
const deepCloneMenu = (menu) => {
  try {
    return JSON.parse(JSON.stringify(menu));
  } catch (error) {
    console.error("Error cloning menu:", error);
    // Return a simple fallback if cloning fails
    return menu.map(item => ({
      ...item,
      subMenu: item.subMenu ? [...item.subMenu] : undefined,
      // Don't include React elements in the clone
      icon: undefined
    }));
  }
};

const getFilteredMenu = (fullMenu, accessTabs) => {
  if (!accessTabs) return fullMenu; // Return full menu if no access tabs (like for super admin)

  // Create a deep copy of the full menu (without React elements)
  const menuCopy = deepCloneMenu(fullMenu);
  
  return menuCopy
    .filter(menuItem => {
      if (menuItem.label === "Logout") return true;

      const hasAccess = accessTabs.some(tab => tab.name === menuItem.label);

      if (!hasAccess) return false;

      if (menuItem.subMenu) {
        const accessTab = accessTabs.find(tab => tab.name === menuItem.label);
        const allowedSubmenu = accessTab?.submenu || [];

        const filteredSubMenu = menuItem.subMenu.filter(subItem =>
          allowedSubmenu.some(allowed => allowed.name === subItem.label)
        );

        if (filteredSubMenu.length > 0) {
          menuItem.subMenu = filteredSubMenu;
          return true;
        }

        return false;
      }

      return true;
    });
};

const MySidebar = () => {
  const { user } = useSelector((state) => state?.auth);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [activeMenu, setActiveMenu] = useState("Dashboard");
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const sidebarRef = useRef(null);

  const { isSidebarOpen } = useSelector((state) => state.loader);

  // Map the filtered menu structure back to the original with icons
  const mapMenuStructure = (filteredItems) => {
    return filteredItems.map(filteredItem => {
      // Find the original menu item to get its icon
      const originalItem = sidebarMenu.find(item => item.label === filteredItem.label);
      
      return {
        ...filteredItem,
        icon: originalItem?.icon,
        subMenu: filteredItem.subMenu 
          ? filteredItem.subMenu.map(subItem => {
              const originalSubItem = originalItem?.subMenu?.find(
                item => item.label === subItem.label
              );
              return { ...subItem, icon: originalSubItem?.icon };
            })
          : undefined
      };
    });
  };

  // Recalculate filtered menu whenever user changes
  useEffect(() => {
    if (user?.role === "admin") {
      // For admin, use the original sidebar menu
      setFilteredMenu([...sidebarMenu]);
    } else {
      // For other roles, filter the menu based on access tabs
      const filtered = getFilteredMenu(sidebarMenu, user?.access_tabs);
      // Map back the icons that were lost in the deep clone
      const mappedMenu = mapMenuStructure(filtered);
      setFilteredMenu(mappedMenu);
    }
  }, [user]);

  const handleMenuClick = (menu, route) => {
    if (route === "/logout") {
      clearCookies();
      dispatch(logout());
      navigate("/login");
    }
    setActiveMenu(menu);
    if (route) {
      const scrollTop = sidebarRef.current?.scrollTop;
      navigate(route, { state: { scrollTop } });
    }
  };

  const handleSubmenuToggle = (label) => {
    setOpenSubmenu((prev) => (prev === label ? null : label));
  };

  const isActiveRoute = (route) => {
    return location.pathname === route;
  };

  const isSubmenuActive = (subMenu) => {
    return subMenu && subMenu.some((subItem) => isActiveRoute(subItem.route));
  };

  React.useEffect(() => {
    if (location.state?.scrollTop && sidebarRef.current) {
      sidebarRef.current.scrollTop = location.state.scrollTop;
    }
  }, [location.state]);

  return (
    <div className="flex flex-col h-screen">
      {/* Brand Logo - Sticky at the top */}
      <div
        className="sticky top-0 z-10 shadow-sm transition-all duration-300"
        style={{
          width: isSidebarOpen ? "210px" : "68px",
        }}
      >
        <div
          className="flex justify-center items-center px-2 border-r border-gray-200 transition-all duration-300"
          style={{ minHeight: "60px" }}
        >
          <img
            src={images.brandLogo}
            alt="Brand Logo"
            className={isSidebarOpen ? "h-9" : "h-7"}
            style={{ transition: "height 0.3s ease-in-out" }}
          />
        </div>
        <div className="border-b border-gray-200"></div>
      </div>

      <Sidebar
        collapsed={!isSidebarOpen}
        style={{
          minWidth: "68px",
          height: "100vh",
          backgroundColor: "var(--secondary-color)",
          width: isSidebarOpen ? "210px" : "60px",
          overflowY: "auto",
          transition: "width 0.3s ease-in-out",
        }}
        ref={sidebarRef}
      >
        {/* Menu Items */}
        <Menu
          menuItemStyles={{
            button: {
              color: "#000000",
              fontSize: "0.875rem",
              padding: "0.5rem",
              "&:hover": {
                backgroundColor: "transparent",
                color: "var(--primary-color)",
              },
              "&.active": {
                backgroundColor: "transparent",
                color: "var(--primary-color)",
              },
            },
            icon: {
              color: "var(--primary-color)",
              fontSize: "0.875rem",
            },
            subMenuContent: {
              backgroundColor: "var(--secondary-color)",
            },
            subMenu: {
              color: "#000000",
              fontSize: "0.875rem",
              padding: "0.5rem",
              "&:hover": {
                backgroundColor: "transparent",
                color: "var(--primary-color)",
              },
            },
          }}
        >
          {filteredMenu.map((menu) =>
            menu.subMenu ? (
              <SubMenu
                key={menu.label}
                label={isSidebarOpen ? menu.label : ""}
                icon={menu.icon}
                open={openSubmenu === menu.label || isSubmenuActive(menu.subMenu)}
                onOpenChange={() => handleSubmenuToggle(menu.label)}
                style={{ paddingLeft: "2px" }}
                className={`${isSubmenuActive(menu.subMenu) ? "active" : ""} data hover:text-[var(--primary-color)]`}
              >
                {menu.subMenu.map((subMenu) => (
                  <MenuItem
                    key={subMenu.label}
                    active={isActiveRoute(subMenu.route)}
                    onClick={() => handleMenuClick(subMenu.label, subMenu.route)}
                    style={{ paddingLeft: "14px", marginLeft: "10px" }}
                  >
                    <div className="flex items-center hover:text-[var(--primary-color)]">
                      <FaAngleRight
                        className="mr-1"
                        style={{ color: isActiveRoute(subMenu.route) ? "var(--primary-color)" : "#000000", fontSize: "0.875rem" }}
                      />
                      <span
                        className={`text-sm ${
                          isActiveRoute(subMenu.route) ? "text-[var(--primary-color)]" : ""
                        } hover:text-[var(--primary-color)]`}
                      >
                        {subMenu.label}
                      </span>
                    </div>
                  </MenuItem>
                ))}
              </SubMenu>
            ) : (
              <MenuItem
                key={menu.label}
                active={isActiveRoute(menu.route)}
                onClick={() => handleMenuClick(menu.label, menu.route)}
                icon={menu.icon}
                style={{ paddingLeft: "2px" }}
                className="hover:text-[var(--primary-color)]"
              >
                {isSidebarOpen && (
                  <span
                    className={`text-sm ${
                      isActiveRoute(menu.route) ? "text-[var(--primary-color)]" : ""
                    } hover:text-[var(--primary-color)]`}
                  >
                    {menu.label}
                  </span>
                )}
              </MenuItem>
            )
          )}
        </Menu>
      </Sidebar>
    </div>
  );
};

export default MySidebar;